import React, { lazy, Suspense, useState } from "react";
import type { HandleWalletSigner } from "../../utils/handle-ownership";
import { normalizeHandle } from "../../utils/handle-resolver";
import { handleNetworkLabel } from "../../utils/handle-networks";
import {
  HANDLE_RENEW_WITHIN_SECONDS,
  type ClaimedHandleDetails,
} from "../../utils/handle-registration-hooks";
const HandleOwnerWalletPicker = lazy(() => import("./HandleOwnerWalletPicker"));

export interface BaseHandlePanelProps {
  canReceivePrivate: boolean;
  busy?: boolean;
  registering?: boolean;
  claimedHandle: string | null;
  handleInput: string;
  setHandleInput: (value: string) => void;
  registerHandle: (handle?: string, options?: { link?: boolean }) => void;
  checkHandleAvailability: () => void | Promise<void>;
  copyHandle: () => void | Promise<void>;
  handleStatus: string | null;
  /** This page's network, e.g. "DogeOS Chikyu". */
  networkLabel?: string;
  claimedDetails?: ClaimedHandleDetails | null;
  /** A handle this wallet owns that this network doesn't receive on yet. */
  suggestedHandle?: string | null;
  findOwnedHandle?: (source?: HandleWalletSigner) => void | Promise<void>;
  /** Renews; with `share`, also sets the handle up for other networks. */
  updateClaimedHandle?: (share?: boolean) => void | Promise<void>;
  /** This page holds the handle's secret and hasn't shared it with the
   * other networks yet. */
  canShareAcrossNetworks?: boolean;
  now?: () => number;
}

export function BaseHandlePanel(props: BaseHandlePanelProps) {
  const [findElsewhere, setFindElsewhere] = useState(false);
  const network = props.networkLabel || "this network";
  const disabled = !props.canReceivePrivate || !!props.busy;
  if (props.claimedHandle) {
    const details =
      props.claimedDetails?.handle === props.claimedHandle
        ? props.claimedDetails
        : null;
    const nowSeconds = Math.floor((props.now?.() ?? Date.now()) / 1000);
    const upgrade = !!props.canShareAcrossNetworks;
    const renew =
      !!details &&
      details.expiresAt - nowSeconds <= HANDLE_RENEW_WITHIN_SECONDS;
    return (
      <div className="wallet-handle-panel">
        <p className="wallet-account-name">{props.claimedHandle}</p>
        {details && (
          <p className="wallet-review-context">
            Receives on {details.networks.map(handleNetworkLabel).join(", ")}.
            {details.expiresAt <= nowSeconds
              ? " Its record has expired, so payments can't reach it until you renew."
              : ""}
          </p>
        )}
        <p className="wallet-review-context">
          This handle belongs to your account permanently. To choose a different
          handle, create a new account.
        </p>
        <p className="wallet-review-context">
          {upgrade
            ? "Set it up once to use it on every StealthOS network."
            : "On another network, choose Find my handle or Find with another wallet to link your receiving account."}
        </p>
        <div className="wallet-handle-actions">
          <button
            type="button"
            className="wallet-primary"
            onClick={() => void props.copyHandle()}
          >
            Copy handle
          </button>
          {(upgrade || renew) && props.updateClaimedHandle && (
            <button
              type="button"
              className="wallet-secondary"
              disabled={disabled}
              onClick={() => void props.updateClaimedHandle?.(upgrade)}
            >
              {upgrade ? "Use on all networks" : "Renew"}
            </button>
          )}
        </div>
        {props.handleStatus && (
          <p className="wallet-handle-status" role="status">
            {props.handleStatus}
          </p>
        )}
      </div>
    );
  }
  const suggested = props.suggestedHandle;
  return (
    <div className="wallet-handle-panel">
      {findElsewhere && props.findOwnedHandle && (
        <Suspense fallback={<p role="status">Loading wallets…</p>}>
          <HandleOwnerWalletPicker
            onPick={props.findOwnedHandle}
            onClose={() => setFindElsewhere(false)}
          />
        </Suspense>
      )}
      {suggested && (
        <div className="wallet-handle-suggestion">
          <p className="wallet-account-name">{suggested}</p>
          <p className="wallet-review-context">
            You already own this handle. Set it up to receive on {network} too.
          </p>
          <div className="wallet-handle-actions">
            <button
              type="button"
              className="wallet-primary"
              disabled={disabled}
              onClick={() => props.registerHandle(suggested, { link: true })}
            >
              {props.registering
                ? "Registering…"
                : `Use ${suggested} on ${network}`}
            </button>
          </div>
        </div>
      )}
      <label htmlFor="wallet-handle-input">.stealth handle</label>
      {props.findOwnedHandle && (
        <button
          type="button"
          className="wallet-secondary"
          disabled={disabled}
          onClick={() => setFindElsewhere(true)}
        >
          Find with another wallet
        </button>
      )}
      <input
        id="wallet-handle-input"
        value={props.handleInput}
        onChange={(event) => props.setHandleInput(event.target.value)}
        onBlur={() => {
          const handle = normalizeHandle(props.handleInput);
          if (handle) props.setHandleInput(handle);
        }}
        disabled={disabled}
        placeholder="mark or mark.stealth"
        autoComplete="off"
      />
      <p className="wallet-review-context">
        A handle works on every StealthOS network: a name you register here is
        yours everywhere. Ownership is verified on-chain. Your spending keys
        stay in your browser.
      </p>
      <p className="wallet-review-context">
        Choose carefully. You can claim one handle per account, and it cannot be
        changed later.
      </p>
      <div className="wallet-handle-actions">
        <button
          type="button"
          className={suggested ? "wallet-secondary" : "wallet-primary"}
          disabled={disabled}
          aria-busy={!!props.registering}
          onClick={() => props.registerHandle()}
        >
          {props.registering ? "Registering…" : "Register"}
        </button>
        <button
          type="button"
          className="wallet-secondary"
          disabled={disabled}
          onClick={() => void props.checkHandleAvailability()}
        >
          Check availability
        </button>
        {props.findOwnedHandle && !suggested && (
          <button
            type="button"
            className="wallet-secondary"
            disabled={disabled}
            onClick={() => void props.findOwnedHandle?.()}
          >
            Find my handle
          </button>
        )}
      </div>
      {props.handleStatus && (
        <p className="wallet-handle-status" role="status">
          {props.handleStatus}
        </p>
      )}
    </div>
  );
}
